/**
 * React Query hooks over `book-management-api.ts`: create-with-inventory, the
 * issue and return workflows, bulk operations and workflow history.
 */

import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  bookManagementApi,
  type BookWithAvailability,
  type BookCopyWithDetails,
  type BookManagementError,
  type CreateBookWithInventoryData,
  type CreateBookWithInventoryResponse,
  type BulkBookOperationsData,
  type BulkOperationResult,
  type BookManagementSearchParams,
  type BookManagementAnalytics,
  type AnalyticsQueryParams,
} from '@/services/book-management-api';

// Import related query keys for cache invalidation
import { catalogQueryKeys } from './catalog-hooks';
import { inventoryQueryKeys } from './inventory-hooks';
import { loanQueryKeys } from './loans-hooks';

/**
 * Query key factories for book management
 */
export const bookManagementQueryKeys = {
  all: ['book-management'] as const,

  // Books with availability
  booksWithAvailability: {
    all: ['books-with-availability'] as const,
    lists: () => [...bookManagementQueryKeys.booksWithAvailability.all, 'list'] as const,
    list: (params?: BookManagementSearchParams) =>
      [...bookManagementQueryKeys.booksWithAvailability.lists(), params] as const,
  },

  // Copies with details
  copiesWithDetails: {
    all: ['copies-with-details'] as const,
    lists: () => [...bookManagementQueryKeys.copiesWithDetails.all, 'list'] as const,
    list: (params?: BookManagementSearchParams) =>
      [...bookManagementQueryKeys.copiesWithDetails.lists(), params] as const,
  },

  // Analytics
  analytics: {
    all: ['analytics'] as const,
    data: (params?: AnalyticsQueryParams) =>
      [...bookManagementQueryKeys.analytics.all, params] as const,
  },

  // Eligibility checks

  // Recommendations
  recommendations: {
    all: ['recommendations'] as const,
    data: (params?: { type?: string; limit?: number; genre_id?: number }) =>
      [...bookManagementQueryKeys.recommendations.all, params] as const,
  },

  // Advanced search
  advancedSearch: {
    all: ['advanced-search'] as const,
    results: (searchData: any) =>
      [...bookManagementQueryKeys.advancedSearch.all, searchData] as const,
  },

  // Workflow history
  workflowHistory: {
    all: ['workflow-history'] as const,
    lists: () => [...bookManagementQueryKeys.workflowHistory.all, 'list'] as const,
    list: (params?: any) =>
      [...bookManagementQueryKeys.workflowHistory.lists(), params] as const,
  },
} as const;

/**
 * Helper function to invalidate all related caches after book management operations
 */
const invalidateBookManagementCaches = (queryClient: QueryClient) => {
  // Invalidate book management specific caches
  queryClient.invalidateQueries({ queryKey: bookManagementQueryKeys.all });

  // Invalidate related module caches
  queryClient.invalidateQueries({ queryKey: catalogQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.bookCopies.all });
  queryClient.invalidateQueries({ queryKey: loanQueryKeys.all });

  // Invalidate dashboard statistics
  queryClient.invalidateQueries({ queryKey: ['dashboard', 'statistics'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
};

/**
 * Helper function to invalidate specific workflow-related caches
 */
const invalidateWorkflowCaches = (queryClient: QueryClient, workflowType: string) => {
  switch (workflowType) {
    case 'create':
      queryClient.invalidateQueries({ queryKey: catalogQueryKeys.books.all });
      queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.bookCopies.all });
      queryClient.invalidateQueries({ queryKey: bookManagementQueryKeys.booksWithAvailability.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      break;
    case 'issue':
      queryClient.invalidateQueries({ queryKey: loanQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.bookCopies.all });
      queryClient.invalidateQueries({ queryKey: bookManagementQueryKeys.booksWithAvailability.all });
      queryClient.invalidateQueries({ queryKey: bookManagementQueryKeys.copiesWithDetails.all });
      break;
    case 'return':
      queryClient.invalidateQueries({ queryKey: loanQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.bookCopies.all });
      queryClient.invalidateQueries({ queryKey: bookManagementQueryKeys.booksWithAvailability.all });
      queryClient.invalidateQueries({ queryKey: bookManagementQueryKeys.copiesWithDetails.all });
      break;
    case 'bulk':
      invalidateBookManagementCaches(queryClient);
      break;
  }
};

/** Turns an Axios error into a BookManagementError and logs it. */
const handleBookManagementError = (error: AxiosError, operation: string): BookManagementError => {
  console.error(`Book management ${operation} error:`, error);

  const errorData = error.response?.data as any;
  const status = error.response?.status;

  let bookManagementError: BookManagementError;

  // Determine error type based on status and content
  if (status === 400) {
    bookManagementError = {
      type: 'validation',
      code: errorData?.code || 'validation_error',
      message: errorData?.detail || errorData?.message || `Validation failed for ${operation}`,
      details: errorData?.details,
      field: errorData?.field,
    };
  } else if (status === 403) {
    bookManagementError = {
      type: 'permission',
      code: 'permission_denied',
      message: `You don't have permission to ${operation}`,
      details: errorData,
    };
  } else if (status === 409 || errorData?.code?.includes('business_logic')) {
    bookManagementError = {
      type: 'business_logic',
      code: errorData?.code || 'business_logic_error',
      message: errorData?.detail || errorData?.message || `Business rule violation for ${operation}`,
      details: errorData?.details,
    };
  } else {
    bookManagementError = {
      type: 'network',
      code: 'network_error',
      message: errorData?.detail || errorData?.message || `Failed to ${operation}`,
      details: errorData,
    };
  }

  // Show appropriate toast message
  switch (bookManagementError.type) {
    case 'validation':
      toast.error(`Validation Error: ${bookManagementError.message}`);
      break;
    case 'business_logic':
      toast.error(`Operation Failed: ${bookManagementError.message}`);
      break;
    case 'permission':
      toast.error(`Permission Denied: ${bookManagementError.message}`);
      break;
    default:
      toast.error(`Error: ${bookManagementError.message}`);
  }

  return bookManagementError;
};

/**
 * Success handler for book management operations
 */
const handleBookManagementSuccess = (message: string, queryClient: QueryClient, workflowType?: string) => {
  toast.success(message);
  if (workflowType) {
    invalidateWorkflowCaches(queryClient, workflowType);
  } else {
    invalidateBookManagementCaches(queryClient);
  }
};

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Books with their availability annotations
 */
export const useBooksWithAvailability = (params?: BookManagementSearchParams) => {
  return useQuery({
    queryKey: bookManagementQueryKeys.booksWithAvailability.list(params),
    queryFn: () => bookManagementApi.getBooksWithAvailability(params),
    staleTime: 1 * 60 * 1000, // 1 minute - availability changes frequently
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to fetch book copies with detailed information
 */
export const useCopiesWithDetails = (params?: BookManagementSearchParams) => {
  return useQuery({
    queryKey: bookManagementQueryKeys.copiesWithDetails.list(params),
    queryFn: () => bookManagementApi.getCopiesWithDetails(params),
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
  });
};

/**
 * Book management analytics
 */
export const useBookManagementAnalytics = (params?: AnalyticsQueryParams) => {
  return useQuery({
    queryKey: bookManagementQueryKeys.analytics.data(params),
    queryFn: () => bookManagementApi.getAnalytics(params),
    staleTime: 5 * 60 * 1000, // 5 minutes - analytics don't change as frequently
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
};

/**
 * Hook to get book recommendations for librarians
 */
export const useBookRecommendations = (params?: {
  type?: 'acquisition' | 'popular' | 'underutilized';
  limit?: number;
  genre_id?: number;
}) => {
  return useQuery({
    queryKey: bookManagementQueryKeys.recommendations.data(params),
    queryFn: () => bookManagementApi.getRecommendations(params),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook for advanced search with multiple criteria
 */
export const useAdvancedBookSearch = (searchData: {
  query?: string;
  filters: BookManagementSearchParams;
  sort_by?: 'title' | 'author' | 'publication_date' | 'popularity' | 'availability';
  sort_order?: 'asc' | 'desc';
}, enabled = true) => {
  return useQuery({
    queryKey: bookManagementQueryKeys.advancedSearch.results(searchData),
    queryFn: () => bookManagementApi.advancedSearch(searchData),
    enabled: enabled && (!!searchData.query || Object.keys(searchData.filters).length > 0),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to get workflow history for audit purposes
 * Backend endpoint implemented - now fully functional!
 */
export const useWorkflowHistory = (params?: {
  workflow_type?: 'create' | 'issue' | 'return' | 'bulk' | 'quick_issue' | 'quick_return';
  user_id?: number;
  status?: 'success' | 'error' | 'partial';
  date_from?: string;
  date_to?: string;
  book_id?: number;
  member_id?: number;
  loan_id?: number;
  page?: number;
  page_size?: number;
  ordering?: string;
}, enabled = true) => {
  return useQuery({
    queryKey: bookManagementQueryKeys.workflowHistory.list(params),
    queryFn: () => bookManagementApi.getWorkflowHistory(params),
    staleTime: 5 * 60 * 1000, // 5 minutes - audit data doesn't change frequently
    enabled: enabled,
  });
};

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Mutation hook to create book with automatic inventory generation
 */
export const useCreateBookWithInventory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBookWithInventoryData) =>
      bookManagementApi.createBookWithInventory(data),
    onSuccess: (response) => {
      handleBookManagementSuccess(
        `Successfully created "${response.book.title}" with ${response.created_copies_count} copies`,
        queryClient,
        'create'
      );
    },
    onError: (error: AxiosError) => {
      handleBookManagementError(error, 'create book with inventory');
    },
  });
};

/**
 * Optimistic mutation hook to create book with inventory
 * Provides instant UI feedback with rollback on error
 * 
 * @example
 * const createBookMutation = useCreateBookWithInventoryOptimistic();
 * await createBookMutation.mutateAsync({
 *   book: { title: 'New Book', isbn: '1234567890', ... },
 *   num_copies: 5,
 *   location_id: 1
 * });
 */
export const useCreateBookWithInventoryOptimistic = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBookWithInventoryData) =>
      bookManagementApi.createBookWithInventory(data),
    onMutate: async (newBookData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['books'] });
      await queryClient.cancelQueries({ queryKey: ['catalog'] });

      // Snapshot the previous values
      const previousBooks = queryClient.getQueryData(['books']);
      const previousCatalog = queryClient.getQueryData(['catalog']);

      // Create a temporary book object for optimistic display
      const tempBook = {
        id: Date.now(), // Temporary ID
        title: newBookData.title,
        isbn: newBookData.isbn,
        subtitle: newBookData.subtitle,
        publication_date: newBookData.publication_date,
        language: newBookData.language,
        pages: newBookData.pages,
        cover_image: null, // File uploads can't be displayed optimistically
        total_copies: newBookData.copies?.length || 0,
        available_copies: newBookData.copies?.length || 0,
        isOptimistic: true, // Flag to show loading state
      };

      // Optimistically update books list
      queryClient.setQueriesData({ queryKey: ['books'] }, (old: any) => {
        if (!old) return { results: [tempBook], count: 1 };
        return {
          ...old,
          results: [tempBook, ...old.results],
          count: old.count + 1,
        };
      });

      // Show loading toast
      toast.loading(`Creating "${newBookData.title}"...`, {
        id: 'create-book-optimistic',
      });

      return { previousBooks, previousCatalog, tempBook };
    },
    onSuccess: (response, variables, context) => {
      // Dismiss loading toast
      toast.dismiss('create-book-optimistic');

      // Show success toast
      handleBookManagementSuccess(
        `Successfully created "${response.book.title}" with ${response.created_copies_count} copies`,
        queryClient,
        'create'
      );

      // Remove the temporary book and add the real one
      queryClient.setQueriesData({ queryKey: ['books'] }, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          results: old.results.map((book: any) =>
            book.id === context?.tempBook.id ? response.book : book
          ),
        };
      });

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['books'] });
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error: AxiosError, variables, context) => {
      // Dismiss loading toast
      toast.dismiss('create-book-optimistic');

      // Rollback optimistic updates
      if (context?.previousBooks) {
        queryClient.setQueryData(['books'], context.previousBooks);
      }
      if (context?.previousCatalog) {
        queryClient.setQueryData(['catalog'], context.previousCatalog);
      }

      // Show error toast
      handleBookManagementError(error, 'create book with inventory');
    },
  });
};

/**
 * Mutation hook for bulk operations on books and copies
 */
export const useBulkBookOperations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkBookOperationsData) =>
      bookManagementApi.bulkOperations(data),
    onSuccess: (result) => {
      const successMessage = `Bulk ${result.operation_type} completed: ${result.successful_items}/${result.total_items} items processed successfully`;

      if (result.failed_items > 0) {
        toast.success(successMessage);
        toast.warning(`${result.failed_items} items failed. Check the results for details.`);
      } else {
        handleBookManagementSuccess(successMessage, queryClient, 'bulk');
      }
    },
    onError: (error: AxiosError) => {
      handleBookManagementError(error, 'perform bulk operations');
    },
  });
};

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for bulk operations with progress tracking
 */
export const useBulkOperations = () => {
  const bulkOperations = useBulkBookOperations();

  const performBulkOperation = async (data: BulkBookOperationsData) => {
    const result = await bulkOperations.mutateAsync(data);
    return result;
  };

  return {
    performBulkOperation,
    isLoading: bulkOperations.isPending,
    error: bulkOperations.error,
  };
};

/**
 * Hook to get real-time book availability status
 */
export const useBookAvailabilityStatus = (book?: BookWithAvailability) => {
  if (!book) return { status: 'unknown', message: 'Book not found' };

  if (book.available_copies > 0) {
    return {
      status: 'available',
      message: `${book.available_copies} of ${book.total_copies} available`,
      color: 'green',
    };
  } else if (book.total_copies > 0) {
    return {
      status: 'unavailable',
      message: `All ${book.total_copies} copies are borrowed`,
      color: 'red',
      nextAvailable: book.next_available_date,
    };
  } else {
    return {
      status: 'no_copies',
      message: 'No copies in inventory',
      color: 'gray',
    };
  }
};

// Export types for convenience
export type {
  BookWithAvailability,
  BookCopyWithDetails,
  BookManagementError,
  CreateBookWithInventoryData,
  CreateBookWithInventoryResponse,
  BulkBookOperationsData,
  BulkOperationResult,
  BookManagementSearchParams,
  BookManagementAnalytics,
  AnalyticsQueryParams,
};